'use client';

import React from 'react';
import styled from 'styled-components';
import { Typography } from '../Typography/Typography';
import { Button } from '../Button/Button';

export interface TitleSectionProps {
  title: string;
  subtitle?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
  };
  className?: string;
  /**
   * Use DataTable styling (compact, with border, uppercase title)
   * @default true
   */
  compact?: boolean;
}

const SectionContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ $compact }) => $compact ? '0' : '32px'};
  padding-bottom: ${({ $compact }) => $compact ? '0' : '16px'};
  border-bottom: ${({ $compact }) => $compact ? 'none' : '1px solid #e5e7eb'};
  border-left: ${({ $compact }) => $compact ? '6px solid #FB6F2A' : 'none'};
  padding-left: ${({ $compact }) => $compact ? '12px' : '0'};
`;

const TitleContent = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  /* Style the title text when compact */
  ${({ $compact }) => $compact && `
    h1, h2 {
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-size: 16px;
      font-weight: 600;
    }
  `}
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
`;

/**
 * TitleSection Component
 * 
 * A page header component with title, subtitle, and optional action button
 * 
 * @component
 * @example
 * ```tsx
 * // Standard page header
 * <TitleSection
 *   title="Priority Matrix Configuration"
 *   subtitle="Gives Super Admin a snapshot of all categories with quick actions."
 *   compact={false}
 *   action={{
 *     label: 'Configure',
 *     onClick: () => console.log('Configure clicked')
 *   }}
 * />
 * 
 * // Compact mode for DataTable (default)
 * <TitleSection
 *   title="Incidents"
 *   subtitle="Total: 13 incidents"
 *   compact={true}
 * />
 * ```
 */
export const TitleSection: React.FC<TitleSectionProps> = ({
  title,
  subtitle,
  action,
  className,
  compact = true,
}) => {
  return (
    <SectionContainer className={className} $compact={compact}>
      <TitleContent $compact={compact}>
        <Typography variant="heading-large" as="h2">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body-regular">
            {subtitle}
          </Typography>
        )}
      </TitleContent>
      
      {action && (
        <ActionContainer>
          <Button
            variant={action.variant || 'primary'}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        </ActionContainer>
      )}
    </SectionContainer>
  );
};

export default TitleSection;
